import { Separator } from "@/components/ui/separator";
import { Facebook, Instagram, Linkedin } from "lucide-react";
import stGeorgeLogoLight from "@/assets/st-george-real-estate-logo-light.webp";
import { trackCallClick } from "@/lib/analytics";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-brand-charcoal border-t border-border">
      <div className="container mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 md:gap-8">
          {/* Logo & Description */}
          <div className="sm:col-span-2">
            <div className="flex flex-col gap-2">
              <a href="/">
                <img 
                  src={stGeorgeLogoLight} 
                  alt="St. George Utah real estate homes for sale - Neil Gibbs Realty"
                  className="h-16 w-auto"
                  loading="lazy"
                  width={128}
                  height={128}
                />
              </a>
            </div>
            <p className="text-white/80 text-luxury leading-relaxed max-w-md mt-4 md:mt-6 text-sm md:text-base">
              Local expertise. Proven results.
              <br />
              Helping buyers and sellers navigate Southern Utah real estate with confidence.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white heading-luxury text-base md:text-lg mb-3 md:mb-4">
              QUICK LINKS
            </h3>
            <ul className="space-y-2 md:space-y-3">
              <li>
                <a href="/buy" className="text-white/80 hover:text-primary transition-colors text-luxury text-sm md:text-base">
                  Buy Properties
                </a>
              </li>
              <li>
                <a href="/sell" className="text-white/80 hover:text-primary transition-colors text-luxury text-sm md:text-base">
                  Sell Your Home
                </a>
              </li>
              <li>
                <a href="/about" className="text-white/80 hover:text-primary transition-colors text-luxury text-sm md:text-base">
                  About Us
                </a>
              </li>
              <li>
                <a href="/communities" className="text-white/80 hover:text-primary transition-colors text-luxury text-sm md:text-base">
                  Communities
                </a>
              </li>
            </ul>
          </div>

          {/* Communities */}
          <div>
            <h3 className="text-white heading-luxury text-base md:text-lg mb-3 md:mb-4">
              COMMUNITIES
            </h3>
            <ul className="space-y-2 md:space-y-3">
              <li><a href="/communities/st-george-utah" className="text-white/80 hover:text-primary transition-colors text-luxury text-sm md:text-base">St. George</a></li>
              <li><a href="/communities/washington-utah" className="text-white/80 hover:text-primary transition-colors text-luxury text-sm md:text-base">Washington</a></li>
              <li><a href="/communities/ivins-utah" className="text-white/80 hover:text-primary transition-colors text-luxury text-sm md:text-base">Ivins</a></li>
              <li><a href="/communities/santa-clara-utah" className="text-white/80 hover:text-primary transition-colors text-luxury text-sm md:text-base">Santa Clara</a></li>
              <li><a href="/communities/hurricane-utah" className="text-white/80 hover:text-primary transition-colors text-luxury text-sm md:text-base">Hurricane</a></li>
              <li><a href="/communities/little-valley-utah" className="text-white/80 hover:text-primary transition-colors text-luxury text-sm md:text-base">Little Valley</a></li>
              <li><a href="/communities/desert-color-utah" className="text-white/80 hover:text-primary transition-colors text-luxury text-sm md:text-base">Desert Color</a></li>
              <li><a href="/communities/sunriver-utah" className="text-white/80 hover:text-primary transition-colors text-luxury text-sm md:text-base">SunRiver</a></li>
              <li><a href="/communities/entrada-utah" className="text-white/80 hover:text-primary transition-colors text-luxury text-sm md:text-base">Entrada</a></li>
              <li><a href="/communities/kayenta-utah" className="text-white/80 hover:text-primary transition-colors text-luxury text-sm md:text-base">Kayenta</a></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white heading-luxury text-base md:text-lg mb-3 md:mb-4">
              CONTACT
            </h3>
            <div className="space-y-2 md:space-y-3 text-white/80 text-luxury text-sm md:text-base">
              <p>St. George, UT 84770</p>
              <a href="tel:+14357034422" className="block hover:text-primary transition-colors" onClick={() => trackCallClick("footer")}>
                (435) 703-4422
              </a>
              <p>utahgibbsteam@gmail.com</p>
            </div>
            
            {/* Social Media Links */}
            <div className="mt-6">
              <h4 className="text-white heading-luxury text-sm mb-3">CONNECT WITH US</h4>
              <div className="flex gap-4">
                <a href="https://www.facebook.com/Britishwine/" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-primary transition-colors" aria-label="Facebook">
                  <Facebook className="h-5 w-5" />
                </a>
                <a href="https://www.instagram.com/neilgibbs.realtor/?hl=en" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-primary transition-colors" aria-label="Instagram">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href="https://www.linkedin.com/in/gibbsn/" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-primary transition-colors" aria-label="LinkedIn">
                  <Linkedin className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
        </div>

        <Separator className="my-6 md:my-8 bg-white/20" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/60 text-luxury text-xs md:text-sm text-center md:text-left">
            © {currentYear} St. George Real Estate. All rights reserved.
          </p>
          
          <div className="flex flex-wrap gap-4 md:gap-6 justify-center md:justify-end text-white/60 text-xs md:text-sm">
            <a href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</a>
            
            <a href="/fair-housing" className="hover:text-white transition-colors">Fair Housing</a>
            <a href="/accessibility-statement" className="hover:text-white transition-colors">Accessibility</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
